import { ART_PAL, ball, body, edge, fade, gloss, mix, n, shade, shadowInk, svgDoc, tint, warm, type ArtPal, type ArtTheme } from './covers/kit';

/**
 * Red envelope (红包) of the daily bonus in culture cn — "premium casual" like the covers: soft volume, one light
 * source top-left (highlight), soft ground shadow, gold embossed lattice and a gold coin seal. No characters or
 * lettering — labels and amounts come from i18n. 96×128. `closed` = a future day, muted.
 */
export type EnvelopeState = 'sealed' | 'open' | 'closed';

const W = 96;
const H = 128;
/** envelope body: x 12…84, y 22…116 */
const X0 = 12;
const X1 = 84;
const Y0 = 22;
const Y1 = 116;
const R = 9;

/** Rounded body outline, optionally with the top edge replaced by `top` (a path from the left to the right edge). */
const bodyPath = (top = `M${X0} ${Y0 + R}Q${X0} ${Y0} ${X0 + R} ${Y0}H${X1 - R}Q${X1} ${Y0} ${X1} ${Y0 + R}`) =>
  `${top}V${Y1 - R}Q${X1} ${Y1} ${X1 - R} ${Y1}H${X0 + R}Q${X0} ${Y1} ${X0} ${Y1 - R}Z`;

/** Diamond lattice (lower part of the front), path shifted by dx/dy — drawn twice for the embossed look. */
function latticeD(dx: number, dy: number): string {
  let d = '';
  let row = 0;
  for (let y = 76; y <= 106; y += 7.5, row++)
    for (let x = 23 + (row % 2) * 5; x <= 74; x += 10) {
      const cx = x + dx;
      const cy = y + dy;
      d += `M${n(cx)} ${n(cy - 2.6)}L${n(cx + 2.6)} ${n(cy)}L${n(cx)} ${n(cy + 2.6)}L${n(cx - 2.6)} ${n(cy)}Z`;
    }
  return d;
}

/** Embossed gold: a darker copy below-right (depth) + the gold shape + a thin light edge above-left. */
function emboss(d: (dx: number, dy: number) => string, red: string, gold: string, P: ArtPal, o: number): string {
  return (
    `<path d="${d(0.8, 0.8)}" fill="${shade(red, 0.45)}" fill-opacity="${n(o * 0.9)}"/>` +
    `<path d="${d(0, 0)}" fill="${gold}" fill-opacity="${o}"/>` +
    `<path d="${d(-0.5, -0.5)}" fill="none" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.18 : 0.3}" stroke-width=".6"/>`
  );
}

/** Coin seal with a square hole (a plain coin, no characters). */
function seal(p: string, P: ArtPal, cx: number, cy: number, r: number, red: string, gold: string): string {
  const h = r * 0.34;
  return (
    `<circle cx="${cx + 1}" cy="${cy + 2}" r="${r}" fill="${shade(red, 0.55)}" fill-opacity=".45"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#${p}coin)" stroke="${edge(gold, P)}" stroke-width="1.6"/>` +
    `<circle cx="${cx}" cy="${cy}" r="${n(r * 0.72)}" fill="none" stroke="${shade(gold, 0.28)}" stroke-width="1.2"/>` +
    `<rect x="${n(cx - h)}" y="${n(cy - h)}" width="${n(h * 2)}" height="${n(h * 2)}" rx="1" fill="${shade(red, 0.25)}" stroke="${shade(gold, 0.35)}" stroke-width="1"/>` +
    gloss(cx - r * 0.38, cy - r * 0.42, r * 0.34, r * 0.17, -35, 0.7)
  );
}

export function envelopeMarkup(state: EnvelopeState, P: ArtPal, p: string): string {
  // a future day is muted: the red is pulled towards grey (and the whole art is half transparent below)
  const red = state === 'closed' ? mix(P.red, P.dark ? '#6B7280' : '#9CA3AF', 0.45) : P.red;
  const gold = state === 'closed' ? mix(warm(P), P.dark ? '#9CA3AF' : '#B0B6C0', 0.45) : warm(P);
  const edgeC = edge(red, P);
  const defs =
    `<defs>` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.6 : 0.22) +
    body(`${p}b`, red, 0.28, 0.4) +
    body(`${p}f`, shade(red, 0.2), 0.14, 0.3, 0.5, 0.9) +
    body(`${p}in`, shade(red, 0.35), 0.1, 0.3) +
    ball(`${p}coin`, gold, 0.5, 0.35) +
    body(`${p}card`, tint(gold, P.dark ? 0.72 : 0.78), 0.4, 0.12) +
    `</defs>`;

  const ground = `<ellipse cx="${W / 2 + 2}" cy="${Y1 + 5}" rx="38" ry="5" fill="url(#${p}sh)"/>`;
  const frame =
    `<rect x="${X0 + 5.8}" y="${Y0 + 5.8}" width="${X1 - X0 - 11}" height="${Y1 - Y0 - 11}" rx="5" fill="none" stroke="${shade(red, 0.45)}" stroke-opacity=".7" stroke-width="1.4"/>` +
    `<rect x="${X0 + 5}" y="${Y0 + 5}" width="${X1 - X0 - 10}" height="${Y1 - Y0 - 10}" rx="5" fill="none" stroke="${gold}" stroke-opacity=".85" stroke-width="1.2"/>`;
  const pattern = emboss(latticeD, red, gold, P, P.dark ? 0.55 : 0.65);
  const blick = gloss(X0 + 16, Y0 + 30, 13, 5, -52, P.dark ? 0.22 : 0.32);

  if (state === 'open') {
    // back panel, flap folded up behind, a gold card with a coin peeking out, the front pocket over it
    const flapTop = 2;
    const back = `<path d="${bodyPath()}" fill="url(#${p}in)" stroke="${edgeC}" stroke-width="2"/>`;
    const flap =
      `<path d="M${X0} ${Y0 + 4}Q${X0 + 2} ${Y0 - 2} ${X0 + 8} ${Y0 - 5}L${W / 2 - 5} ${flapTop + 1}Q${W / 2} ${flapTop - 1} ${W / 2 + 5} ${flapTop + 1}L${X1 - 8} ${Y0 - 5}Q${X1 - 2} ${Y0 - 2} ${X1} ${Y0 + 4}Z" fill="url(#${p}f)" stroke="${edgeC}" stroke-width="2" stroke-linejoin="round"/>` +
      `<path d="M${X0 + 9} ${Y0 - 1}L${W / 2} ${flapTop + 6}L${X1 - 9} ${Y0 - 1}" fill="none" stroke="${gold}" stroke-opacity=".75" stroke-width="1.2" stroke-linejoin="round"/>`;
    const cardX = X0 + 9;
    const cardY = Y0 - 6;
    const cardW = X1 - X0 - 18;
    const card =
      `<rect x="${cardX}" y="${cardY}" width="${cardW}" height="44" rx="5" fill="url(#${p}card)" stroke="${edge(gold, P)}" stroke-width="1.6"/>` +
      `<rect x="${cardX + 4}" y="${cardY + 4}" width="${cardW - 8}" height="36" rx="3" fill="none" stroke="${gold}" stroke-opacity=".7" stroke-width="1"/>` +
      seal(p, P, W / 2, cardY + 17, 9, red, gold) +
      gloss(cardX + 9, cardY + 7, 7, 2.6, -25, 0.6);
    const pocketTop = `M${X0} ${Y0 + 22}L${W / 2} ${Y0 + 36}L${X1} ${Y0 + 22}`;
    const pocket =
      `<path d="${bodyPath(pocketTop)}" fill="url(#${p}b)" stroke="${edgeC}" stroke-width="2" stroke-linejoin="round"/>` +
      `<path d="M${X0 + 6} ${Y0 + 29}L${W / 2} ${Y0 + 42}L${X1 - 6} ${Y0 + 29}" fill="none" stroke="${gold}" stroke-opacity=".85" stroke-width="1.2" stroke-linejoin="round"/>`;
    return defs + ground + flap + back + card + pocket + pattern + gloss(X0 + 14, Y0 + 50, 10, 4, -52, P.dark ? 0.2 : 0.3);
  }

  // sealed / closed: front with the flap down and the coin seal on its tip
  const tipY = Y0 + 46;
  const front = `<path d="${bodyPath()}" fill="url(#${p}b)" stroke="${edgeC}" stroke-width="2"/>`;
  const flapD = `M${X0} ${Y0 + R}Q${X0} ${Y0} ${X0 + R} ${Y0}H${X1 - R}Q${X1} ${Y0} ${X1} ${Y0 + R}V${Y0 + 12}Q${W / 2 + 14} ${tipY - 2} ${W / 2} ${tipY}Q${W / 2 - 14} ${tipY - 2} ${X0} ${Y0 + 12}Z`;
  const flap =
    // soft shadow of the flap on the front
    `<path d="M${X0 + 1} ${Y0 + 14}Q${W / 2 - 14} ${tipY + 1} ${W / 2} ${tipY + 3}Q${W / 2 + 14} ${tipY + 1} ${X1 - 1} ${Y0 + 14}" fill="none" stroke="${shade(red, 0.5)}" stroke-opacity=".45" stroke-width="3" stroke-linecap="round"/>` +
    `<path d="${flapD}" fill="url(#${p}f)" stroke="${edgeC}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M${X0 + 2} ${Y0 + 13}Q${W / 2 - 14} ${tipY - 1} ${W / 2} ${tipY + 1}" fill="none" stroke="${tint(red, 0.35)}" stroke-opacity=".6" stroke-width="1"/>` +
    `<path d="M${X0 + 5} ${Y0 + 12}Q${W / 2 - 12} ${tipY - 7} ${W / 2} ${tipY - 5}Q${W / 2 + 12} ${tipY - 7} ${X1 - 5} ${Y0 + 12}" fill="none" stroke="${gold}" stroke-opacity=".85" stroke-width="1.2"/>`;
  const inner = ground + front + frame + pattern + flap + seal(p, P, W / 2, tipY, 11, red, gold) + blick;
  return defs + (state === 'closed' ? `<g opacity=".5">${inner}</g>` : inner);
}

export function envelopeSvg(state: EnvelopeState, theme: ArtTheme, prefix = `env${state[0]}${theme[0]}`): string {
  return svgDoc(W, H, envelopeMarkup(state, ART_PAL[theme], prefix));
}

export const envelopeUri = (state: EnvelopeState, theme: ArtTheme) => 'data:image/svg+xml;utf8,' + encodeURIComponent(envelopeSvg(state, theme));
